/* eslint-disable jsdoc/require-param */
/* eslint-disable jsdoc/check-param-names */
import React from "react";
import { makeStyles } from "@material-ui/styles";
import {
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import VisibilityIcon from "@material-ui/icons/Visibility";
import MovieIcon from "@material-ui/icons/Movie";

const DESCRIPTION_LINE_HEIGHT = 20; // Line height for description text
const MAX_DESCRIPTION_LINES = 2; // Maximum number of lines for description

const useStyles = makeStyles(theme => ({
  card: {
    borderRadius: 8,
    boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    border: "1px solid #e0e0e0",
    marginBottom: theme.spacing(2),
    position: "relative",
    height: "auto",
    minHeight: 340,
    [theme.breakpoints.down("xs")]: {
      minHeight: 320,
    },
  },
  cardPassed: {
    borderTop: "6px solid #32a58e",
  },
  cardNotStarted: {
    borderTop: "6px solid #ff9800",
  },
  cardFailed: {
    borderTop: "6px solid #f44336",
  },
  cardContent: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    height: "100%",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1.5),
    },
  },
  topContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(1.5),
    },
  },
  topLeftContainer: {
    display: "flex",
    alignItems: "center",
  },
  topRightContainer: {
    display: "flex",
    alignItems: "center",
  },
  statusBadge: {
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 30,
    display: "flex",
    alignItems: "center",
    fontSize: "0.75rem",
    fontWeight: 500,
    backgroundColor: "#f5f5f5",
    marginRight: theme.spacing(1),
    whiteSpace: "nowrap",
  },
  passedBadge: {
    backgroundColor: "rgba(50, 165, 142, 0.1)",
    color: "#32a58e",
  },
  notStartedBadge: {
    backgroundColor: "rgba(160, 160, 160, 0.1)",
    color: "#666666",
  },
  failedBadge: {
    backgroundColor: "rgba(244, 67, 54, 0.1)",
    color: "#f44336",
  },
  statusIcon: {
    fontSize: "1rem",
    marginRight: theme.spacing(0.5),
    verticalAlign: "middle",
  },
  iconCircle: {
    backgroundColor: "#f0f9f7",
    borderRadius: "50%",
    width: 50,
    height: 50,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      width: 40,
      height: 40,
      marginRight: theme.spacing(1.5),
    },
  },
  videoIcon: {
    color: "#ff9800",
    fontSize: "2rem",
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.5rem",
    },
  },
  contentSection: {
    flex: "0 0 auto", // Don't allow this section to grow or shrink
  },
  textContent: {
    width: "100%",
  },
  moduleTitle: {
    fontSize: "1.25rem",
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      fontSize: "1.125rem",
    },
  },
  // Category tag styling
  categoryTag: {
    fontSize: "0.5rem",
    fontWeight: 500,
    display: "inline-flex",
    padding: theme.spacing(0.5, 1.5),
    borderRadius: 16,
    marginBottom: theme.spacing(1.5),
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  categoryZawadi: {
    backgroundColor: "rgba(27, 43, 74, 0.1)",
    color: "#1b2b4a",
  },
  categoryTechnical: {
    backgroundColor: "rgba(255, 152, 0, 0.1)",
    color: "#ff9800",
  },
  categoryCompliance: {
    backgroundColor: "rgba(13, 130, 150, 0.1)",
    color: "#0d8296",
  },
  categoryProduct: {
    backgroundColor: "rgba(233, 97, 37, 0.1)",
    color: "#e96125",
  },
  categorySales: {
    backgroundColor: "rgba(121, 80, 242, 0.1)",
    color: "#7950f2",
  },
  categoryDefault: {
    backgroundColor: "#f0f9f7",
    color: "#32a58e",
  },
  moduleDescription: {
    fontSize: "1.25rem",
    color: "#444",
    marginBottom: theme.spacing(0.5),
    display: "-webkit-box",
    WebkitLineClamp: MAX_DESCRIPTION_LINES,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: `${DESCRIPTION_LINE_HEIGHT}px`,
    maxHeight: DESCRIPTION_LINE_HEIGHT * MAX_DESCRIPTION_LINES,
    [theme.breakpoints.down("xs")]: {
      fontSize: "1rem",
      lineHeight: "18px",
      maxHeight: 18 * MAX_DESCRIPTION_LINES,
    },
  },
  progressSection: {
    marginTop: "auto", // Push this to bottom of card
    marginBottom: theme.spacing(2),
    flex: "0 0 auto", // Don't allow this section to grow or shrink
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(1.5),
    },
  },
  progressContainer: {
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("xs")]: {
      marginBottom: theme.spacing(1.5),
    },
  },
  progressLabels: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(0.5),
  },
  progressLabel: {
    fontSize: "0.875rem",
    color: "#666",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.8125rem",
    },
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    [theme.breakpoints.down("xs")]: {
      height: 6,
    },
  },
  metadataContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1),
  },
  metadataLeft: {
    display: "flex",
    flexDirection: "column",
  },
  metadataRight: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  attemptsText: {
    fontSize: "0.875rem",
    color: "#666",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.8125rem",
    },
  },
  completedText: {
    fontSize: "0.875rem",
    color: "#666",
    textAlign: "right",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.8125rem",
    },
  },
  actionButtonContainer: {
    marginTop: theme.spacing(0.5),
    flex: "0 0 auto", // Don't allow this section to grow or shrink
  },
  actionButton: {
    borderRadius: 8,
    padding: theme.spacing(1, 3),
    textTransform: "none",
    fontWeight: 500,
    fontSize: "1rem",
    minWidth: 120,
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(0.75, 2),
      fontSize: "0.9375rem",
    },
  },
  reviewButton: {
    backgroundColor: "#32a58e",
    color: "white",
    "&:hover": {
      backgroundColor: "#288a74",
    },
  },
  startButton: {
    backgroundColor: "#1b2b4a",
    color: "white",
    "&:hover": {
      backgroundColor: "#1b2b4a",
    },
  },
  continueButton: {
    backgroundColor: "#4285F4",
    color: "white",
    "&:hover": {
      backgroundColor: "#3b77db",
    },
  },
  buttonIcon: {
    marginRight: theme.spacing(1),
    fontSize: "1rem",
    [theme.breakpoints.down("xs")]: {
      fontSize: "0.9375rem",
      marginRight: theme.spacing(0.75),
    },
  },
  // Placeholder element when no progress is available
  placeholderProgress: {
    height: 54, // Height to match progress indicator section
    [theme.breakpoints.down("xs")]: {
      height: 45,
    },
  },
}));

/**
 * Displays a single training module as a card, including its name, description,
 * type, and progress information. The card also includes a button to start,
 * continue, or review the module based on its status.
 *
 * @param {object} module - The training module to display
 * @returns {React.Element} The module card component
 */
function ModuleCard({ module }) {
  const classes = useStyles();

  const hasPassed = module.training_user_attempt_index.some(
    attempt => attempt.pass === true
  );

  const hasAttempts = module.training_user_attempt_index.length > 0;

  const bestAttempt = hasAttempts
    ? Math.max(
        ...module.training_user_attempt_index.map(attempt => attempt.percentage)
      ).toFixed(0)
    : 0;

  const lastAttemptDate = hasAttempts
    ? new Date(
        Math.max(
          ...module.training_user_attempt_index.map(attempt =>
            new Date(attempt.created_at).getTime()
          )
        )
      )
    : null;

  const formattedDate = lastAttemptDate
    ? lastAttemptDate
        .toLocaleDateString("en-ZA", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, "/")
    : "";

  /**
   * Determines the appropriate CSS class for the category tag based on category name
   * @param {string} category - The category name
   * @returns {string} The CSS class for styling the category tag
   */
  const getCategoryClass = category => {
    if (!category) return classes.categoryZawadi;

    const categoryLower = category.toLowerCase();

    if (categoryLower.includes("zawadi")) return classes.categoryZawadi;
    if (categoryLower.includes("technical") || categoryLower.includes("tech")) {
      return classes.categoryTechnical;
    }
    if (
      categoryLower.includes("compliance") ||
      categoryLower.includes("legal")
    ) {
      return classes.categoryCompliance;
    }
    if (categoryLower.includes("product")) {
      return classes.categoryProduct;
    }
    if (
      categoryLower.includes("sales") ||
      categoryLower.includes("marketing")
    ) {
      return classes.categorySales;
    }

    // Use colors based on first letter for other categories to ensure consistency
    const firstChar = categoryLower.charAt(0);
    const charCode = firstChar.charCodeAt(0);

    if (charCode % 5 === 0) return classes.categoryZawadi;
    if (charCode % 5 === 1) return classes.categoryTechnical;
    if (charCode % 5 === 2) return classes.categoryCompliance;
    if (charCode % 5 === 3) return classes.categoryProduct;
    if (charCode % 5 === 4) return classes.categorySales;

    return classes.categoryDefault;
  };

  // Set card properties based on status
  let cardClass = "";
  let statusClass = "";
  let statusText = "";
  let StatusIcon = null;
  let actionButtonText = "";
  let actionButtonClass = "";
  let ActionIcon = null;
  const actionButtonLink = `/training-attempt/${module.id}`;

  if (hasPassed) {
    cardClass = classes.cardPassed;
    statusClass = classes.passedBadge;
    statusText = "Passed";
    StatusIcon = CheckCircleIcon;
    actionButtonText = "Review";
    actionButtonClass = classes.reviewButton;
    ActionIcon = VisibilityIcon;
  } else {
    cardClass = classes.cardNotStarted;
    statusClass = classes.notStartedBadge;
    statusText = "Not Started";
    StatusIcon = RadioButtonUncheckedIcon;
    actionButtonText = "Start";
    actionButtonClass = classes.startButton;
    ActionIcon = PlayArrowIcon;
  }

  return (
    <Card className={`${classes.card} ${cardClass}`}>
      <CardContent className={classes.cardContent}>
        {/* Top container with video icon and status badge */}
        <div className={classes.topContainer}>
          {/* Top Left - Video Icon */}
          <div className={classes.topLeftContainer}>
            <div className={classes.iconCircle}>
              <MovieIcon className={classes.videoIcon} />
            </div>
          </div>

          {/* Top Right - Status Badge */}
          <div className={classes.topRightContainer}>
            <div className={`${classes.statusBadge} ${statusClass}`}>
              <StatusIcon className={classes.statusIcon} />
              <span>{statusText}</span>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className={classes.contentSection}>
          <div className={classes.textContent}>
            {/* Module Title */}
            <Typography className={classes.moduleTitle}>
              {module.module_name}
            </Typography>

            {/* Module Category as Tag */}
            <div>
              <Typography
                className={`${classes.categoryTag} ${getCategoryClass(
                  module.module_category
                )}`}
                component="span"
              >
                {module.module_category || "ZAWADI RELATED"}
              </Typography>
            </div>

            {/* Module Description with Ellipsis */}
            {module.module_description && (
              <Typography className={classes.moduleDescription}>
                {module.module_description}
              </Typography>
            )}
          </div>
        </div>

        {/* Progress Section */}
        <div className={classes.progressSection}>
          {hasAttempts ? (
            <div className={classes.progressContainer}>
              <div className={classes.progressLabels}>
                <Typography className={classes.progressLabel}>
                  Progress: {bestAttempt}%
                </Typography>
                <Typography className={classes.progressLabel}>
                  Required: {module.pass_threshold}%
                </Typography>
              </div>
              <LinearProgress
                variant="determinate"
                value={Math.min(bestAttempt, 100)}
                className={classes.progressBar}
                color="primary"
              />
            </div>
          ) : (
            <div className={classes.placeholderProgress} />
          )}

          {/* Metadata (Attempts and Completion) */}
          <div className={classes.metadataContainer}>
            <div className={classes.metadataLeft}>
              <Typography className={classes.attemptsText}>
                Attempts:{" "}
                {hasAttempts
                  ? `${module.training_user_attempt_index.length}/5`
                  : "0/5"}
              </Typography>
            </div>
            <div className={classes.metadataRight}>
              {hasPassed && (
                <Typography className={classes.completedText}>
                  Completed: {formattedDate}
                </Typography>
              )}
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className={classes.actionButtonContainer}>
          <Link to={actionButtonLink} style={{ textDecoration: "none" }}>
            <Button
              className={`${classes.actionButton} ${actionButtonClass}`}
              variant="contained"
              disableElevation
            >
              <ActionIcon className={classes.buttonIcon} />
              {actionButtonText}
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

export default ModuleCard;
